import { Link } from "react-router-dom";
import { Phone, MapPin, Heart, Shield, Award, CheckCircle, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Ticker from "@/components/Ticker";
import SEO from "@/components/SEO";
import primroseLogo from "@/assets/primrose-logo.png";
import nhsLogo from "@/assets/nhs-logo.png";
import iconConsultation from "@/assets/icon-consultation.png";
import iconPremiumWig from "@/assets/icon-premium-wig.png";
import iconNhsVoucher from "@/assets/icon-nhs-voucher.png";
import nhsHero from "@/assets/nhs-hero.jpg";

const NHSWigSupplierPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="NHS Wig Supplier — Plymouth Medical Wigs"
        description="Plymhair is an approved NHS wig supplier based in Plymouth. Visit us in-store at Plymhair or call for a private, compassionate consultation."
        url="/nhs-wig-supplier"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Plymhair & Extensions",
          description: "Approved NHS wig supplier offering private consultations and premium medical wigs in Plymouth.",
          telephone: "+44-1752-651631",
          email: "info@plymhair.co.uk",
          image: "https://www.plymhair.co.uk/plymhair-logo.svg",
          url: "https://www.plymhair.co.uk/nhs-wig-supplier",
          address: {
            "@type": "PostalAddress",
            streetAddress: "21 Market Avenue",
            addressLocality: "Plymouth",
            postalCode: "PL1 1PG",
            addressCountry: "GB",
          },
        }}
      />
      <Ticker />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-foreground text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img src={nhsHero} alt="Private wig consultation salon" className="w-full h-full object-cover" width={1920} height={800} />
          <div className="absolute inset-0 bg-foreground/70" />
        </div>
        <div className="relative container max-w-4xl text-center py-28 md:py-40">
          <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm text-primary px-4 py-1.5 rounded-full text-sm font-body font-semibold mb-6">
            <Shield className="w-4 h-4" />
            Approved NHS Wig Supplier
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-tight mb-6">
            Compassionate Care,{" "}
            <span className="italic font-normal">Beautiful Hair</span>
          </h1>
          <p className="font-body text-primary-foreground/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-4">
            We understand that hair loss can be an incredibly personal experience.
            As an approved NHS wig supplier, we're here to help you find the
            perfect wig with warmth, discretion, and expert care.
          </p>
          <p className="font-body text-primary-foreground/65 text-base max-w-2xl mx-auto leading-relaxed">
            All consultations and fittings take place at our salon, where you'll have
            complete privacy and our full attention. For customers who are unable to
            travel, we also offer <strong className="text-primary-foreground/85">home and hospital visits</strong> to ensure
            everyone can access the support they deserve.
          </p>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 md:py-24">
        <div className="container max-w-5xl">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-center mb-12">
            What We Offer
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                image: iconConsultation,
                title: "Private Consultations",
                text: "Every fitting takes place in a comfortable, private setting. We take the time to understand your needs and preferences so you feel completely at ease.",
              },
              {
                image: iconPremiumWig,
                title: "Premium Quality Wigs",
                text: "Our collection includes natural-looking human hair and high-quality synthetic wigs in a wide range of styles, colours, and textures to suit every taste.",
              },
              {
                image: iconNhsVoucher,
                title: "NHS Voucher Accepted",
                text: "We accept NHS wig vouchers (ENG-A or ENG-B), making it straightforward to access the hair you need through the NHS referral process.",
              },
            ].map(({ image, title, text }) => (
              <div
                key={title}
                className="bg-card border border-border rounded-2xl p-8 text-center"
              >
                <img src={image} alt={title} className="w-16 h-16 object-contain mx-auto mb-5" loading="lazy" />
                <h3 className="font-display text-xl font-semibold mb-3">
                  {title}
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="container max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-4">
            How It Works
          </h2>
          <p className="font-body text-muted-foreground text-center mb-12 max-w-xl mx-auto">
            Getting your NHS wig through Plymhair is simple and stress-free.
          </p>

          <div className="space-y-8">
            {[
              {
                step: "1",
                title: "Get Your Referral",
                text: "Your GP, consultant, or hospital team will provide you with an NHS wig voucher (prescription). This is your referral to an approved supplier like us.",
              },
              {
                step: "2",
                title: "Book Your Appointment",
                text: "Give us a call or pop into our salon at Plymhair to book a private fitting appointment at a time that suits you.",
              },
              {
                step: "3",
                title: "Your Private Fitting",
                text: "During your appointment, we'll help you try on different styles and find the wig that makes you feel confident and comfortable. There's no rush — take all the time you need.",
              },
              {
                step: "4",
                title: "Collection & Aftercare",
                text: "Once your wig is ready, we'll fit it perfectly and show you how to care for it. We're always here if you need adjustments or advice down the line.",
              },
            ].map(({ step, title, text }) => (
              <div key={step} className="flex gap-6 items-start">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg shrink-0">
                  {step}
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold mb-1">
                    {title}
                  </h3>
                  <p className="font-body text-muted-foreground leading-relaxed">
                    {text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-12 md:py-16">
        <div className="container max-w-5xl">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { text: "Additional funding for breast cancer patients" },
              { text: "Home & hospital visits available" },
              { text: "Private fitting rooms at our salon" },
            ].map(({ text }) => (
              <div key={text} className="flex items-center gap-3 bg-card border border-border rounded-xl p-5">
                <CheckCircle className="w-6 h-6 text-primary shrink-0" />
                <span className="font-body text-foreground font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Primrose Foundation */}
      <section className="py-16 md:py-24">
        <div className="container max-w-4xl">
          <div className="bg-card border border-border rounded-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <img src={primroseLogo} alt="The Primrose Foundation logo" className="w-24 h-24 object-contain shrink-0 mx-auto md:mx-0" />
              <div className="text-center md:text-left">
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                  The Primrose Foundation
                </h2>
                <p className="font-body text-muted-foreground leading-relaxed">
                  We're proud to work alongside the{" "}
                  <a
                    href="https://www.primrosefoundation.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-semibold inline-flex items-center gap-1"
                  >
                    Primrose Foundation
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  , a charity that supports breast cancer patients across the South West.
                  Through this partnership, eligible customers can receive <strong>additional funding
                  towards their wig purchase</strong> — on top of their NHS voucher — helping to
                  cover the cost of a higher-quality or custom wig that truly makes them feel like themselves again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Useful Links */}
      <section className="py-12 bg-secondary">
        <div className="container max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-center mb-8">Useful Links</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href="https://www.primrosefoundation.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors group"
            >
              <img src={primroseLogo} alt="Primrose Foundation logo" className="w-10 h-10 object-contain shrink-0" />
              <div>
                <p className="font-body font-semibold text-foreground group-hover:text-primary transition-colors">Primrose Foundation</p>
                <p className="font-body text-muted-foreground text-sm">Breast cancer support &amp; wig funding</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground ml-auto shrink-0" />
            </a>
            <a
              href="https://www.plymouthhospitals.nhs.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors group"
            >
              <img src={nhsLogo} alt="NHS logo" className="w-10 h-10 object-contain shrink-0" />
              <div>
                <p className="font-body font-semibold text-foreground group-hover:text-primary transition-colors">NHS Plymouth</p>
                <p className="font-body text-muted-foreground text-sm">University Hospitals Plymouth NHS Trust</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground ml-auto shrink-0" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-foreground text-primary-foreground">
        <div className="container max-w-3xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            Ready to Book Your Consultation?
          </h2>
          <p className="font-body text-primary-foreground/70 text-lg mb-8 max-w-xl mx-auto">
            Call us today or visit our salon to arrange a private consultation.
            We also offer home and hospital visits for those who need them.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+441752651631"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold tracking-wider uppercase px-8 py-4 rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Us
            </a>
            <Link
              to="/shop?category=Wigs"
              className="inline-flex items-center justify-center gap-2 bg-primary-foreground text-foreground font-body text-sm font-semibold tracking-wider uppercase px-8 py-4 rounded-lg hover:bg-primary-foreground/90 transition-colors"
            >
              Browse Our Wigs
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NHSWigSupplierPage;
